// AuthContext.tsx
// Manages authentication state and provides auth methods including password reset.
// Updated: added resetPassword and updatePassword methods for password recovery flow.
// Updated: loadUserProfile is now awaited in getSession before setLoading(false),
//   ensuring userProfile is populated before any page renders (fixes race condition
//   where isAdmin/userProfile was null during first render even after loading=false).
// Updated: UserProfile now includes firstName, lastName, displayName derived from
//   public.users columns. Initials computed from first/last name with email fallback
//   for existing users. signUp now accepts firstName/lastName and passes them via
//   options.data to populate raw_user_meta_data (consumed by handle_new_user trigger).
// Updated: added invitationStatus to UserProfile to support admin-provisioned accounts.
//   markInvitationAccepted sets invitation_status to 'accepted' in public.users after
//   the invited user sets their password on first login. Returns a boolean indicating
//   whether the DB write succeeded; local state is only updated on confirmed success.
// Updated: added logoUrl to UserProfile and refreshProfile method to allow settings
//   page to sync the context after a profile logo update.
// Updated 2026-04-14: getSession error handler calls signOut() on stale refresh tokens
//   to clear localStorage and prevent cascading AuthApiError console errors.
// Updated 2026-04-14: calls setSentryUser on session change so Sentry errors are
//   enriched with the current user's id, email and admin status.
// Updated 2026-04-16: added captureError to errorReporting import so the
//   markInvitationAccepted error path can report to Sentry correctly.
// Updated Apr 2026: added stampTitle and onboardingDismissed to UserProfile for
//   the onboarding card logic; query extended to include stamp_title and onboarding_dismissed.
// Updated May 2026: purgeStaleSession() detects and removes expired/invalid refresh tokens
//   from localStorage before getSession() to prevent the HTTP 400 / AuthApiError cascade.
//   Error handler extended to cover additional refresh token error codes.
// Updated May 2026: removed logEvent('session_started') from loadUserProfile.
//   Session tracking is now handled exclusively by useSessionTracker (ProtectedRoute),
//   avoiding the concurrent double-fire that caused duplicate session counts.

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { getInitials } from '../utils/stringHelpers';
import { setSentryUser, captureError, addBreadcrumb } from '../utils/errorReporting';

export type InvitationStatus = 'pending' | 'accepted' | null;

interface UserProfile {
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  initials: string;
  isAdmin: boolean;
  logoUrl: string | null;
  stampTitle: string | null;
  onboardingDismissed: boolean;
  invitationStatus: InvitationStatus;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userProfile: UserProfile | null;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  markInvitationAccepted: () => Promise<boolean>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STALE_REFRESH_TOKEN_MESSAGES = [
  'Refresh Token Not Found',
  'Invalid Refresh Token',
  'refresh_token_not_found',
  'refresh_token_already_used',
];

function isStaleRefreshTokenError(error: { message?: string; status?: number }): boolean {
  const msg = error.message ?? '';
  return STALE_REFRESH_TOKEN_MESSAGES.some((s) => msg.includes(s));
}

// Purges Supabase session from localStorage if the stored token is already expired,
// preventing an unnecessary HTTP 400 request on app startup.
function purgeStaleSession(supabaseUrl: string): void {
  try {
    const projectRef = new URL(supabaseUrl).hostname.split('.')[0];
    const key = `sb-${projectRef}-auth-token`;
    const raw = localStorage.getItem(key);
    if (!raw) return;

    const parsed = JSON.parse(raw) as { expires_at?: number; refresh_token?: string } | null;
    if (!parsed) return;

    const hasRefreshToken = Boolean(parsed.refresh_token);
    const isExpired = typeof parsed.expires_at === 'number' && parsed.expires_at * 1000 < Date.now();

    if (!hasRefreshToken || isExpired) {
      localStorage.removeItem(key);
    }
  } catch {
    // Silently ignore parse errors; getSession() will handle the rest.
  }
}

function buildUserProfile(data: {
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_admin: boolean | null;
  logo_url: string | null;
  stamp_title: string | null;
  onboarding_dismissed: boolean | null;
  invitation_status: string | null;
}): UserProfile {
  const firstName = data.first_name?.trim() ?? '';
  const lastName = data.last_name?.trim() ?? '';

  let displayName: string;
  let initials: string;

  if (firstName || lastName) {
    displayName = [firstName, lastName].filter(Boolean).join(' ');
    initials = getInitials(displayName);
  } else {
    const prefix = data.email.split('@')[0];
    displayName = prefix.charAt(0).toUpperCase() + prefix.slice(1);
    initials = prefix.slice(0, 2).toUpperCase();
  }

  const invitationStatus = (data.invitation_status === 'pending' || data.invitation_status === 'accepted')
    ? data.invitation_status
    : null;

  return {
    email: data.email,
    firstName,
    lastName,
    displayName,
    initials,
    isAdmin: data.is_admin || false,
    logoUrl: data.logo_url ?? null,
    stampTitle: data.stamp_title ?? null,
    onboardingDismissed: data.onboarding_dismissed ?? false,
    invitationStatus,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const loadUserProfile = useCallback(async (userId: string) => {
    addBreadcrumb('Chargement du profil utilisateur', 'auth', { userId });
    const { data } = await supabase
      .from('users')
      .select('email, first_name, last_name, is_admin, logo_url, stamp_title, onboarding_dismissed, invitation_status')
      .eq('id', userId)
      .maybeSingle();

    if (data && data.email) {
      const profile = buildUserProfile(data);
      setUserProfile(profile);
      setSentryUser({ id: userId, email: profile.email, isAdmin: profile.isAdmin });
      addBreadcrumb('Profil utilisateur chargé', 'auth', { userId, isAdmin: profile.isAdmin });
    }
  }, []);

  useEffect(() => {
    // Remove expired or token-less sessions before the first network call
    // to prevent the HTTP 400 / AuthApiError cascade in the console.
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (supabaseUrl) {
      purgeStaleSession(supabaseUrl);
    }

    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        if (isStaleRefreshTokenError(error)) {
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          setUserProfile(null);
        }
        setLoading(false);
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await loadUserProfile(session.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      (async () => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          await loadUserProfile(session.user.id);
        } else {
          setUserProfile(null);
          setSentryUser(null);
        }
        setLoading(false);
      })();
    });

    return () => subscription.unsubscribe();
  }, [loadUserProfile]);

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  };

  const markInvitationAccepted = async (): Promise<boolean> => {
    if (!user) return false;
    const { data, error } = await supabase
      .from('users')
      .update({ invitation_status: 'accepted' })
      .eq('id', user.id)
      .select('id');
    if (error || !data || data.length === 0) {
      console.error('markInvitationAccepted: DB update failed', error);
      if (error) captureError(error, { context: 'AuthContext', action: 'markInvitationAccepted', userId: user.id });
      return false;
    }
    setUserProfile((prev) =>
      prev ? { ...prev, invitationStatus: 'accepted' } : prev
    );
    return true;
  };

  const refreshProfile = useCallback(async () => {
    if (user) {
      await loadUserProfile(user.id);
    }
  }, [user, loadUserProfile]);

  const value = {
    user,
    session,
    loading,
    userProfile,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    markInvitationAccepted,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
